import { A2uiComponentEntry } from 'types/space';

/**
 * Object keys that could pollute `Object.prototype` if a component's props are ever spread or
 * `Object.assign`-ed by a downstream renderer/adapter (the classic prototype-pollution injection
 * vector for JSON-sourced objects). Structural props are otherwise unconstrained data, but these
 * three keys have no legitimate use in an A2UI prop value.
 */
export const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Depth bound for the PROP-VALUE walk. `maxNestingDepth` bounds the component graph,
 * not the shape of an individual prop value — a single component holding ~10k nested
 * arrays sits well inside `maxPayloadBytes` but blows the stack here, turning the
 * read path's fail-closed exclusion into an uncaught RangeError (and a 500 on
 * create/update). A prop value nested past this is reported as forbidden rather than
 * walked.
 */
const MAX_PROP_VALUE_DEPTH = 64;

/** Finds the JSON-Pointer-ish path of the first forbidden key in a component entry's props, if any. */
export function findForbiddenPropKey(entry: A2uiComponentEntry): string | null {
  const visit = (value: unknown, path: string, depth: number): string | null => {
    if (depth > MAX_PROP_VALUE_DEPTH) return path;
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const found = visit(value[i], `${path}/${i}`, depth + 1);
        if (found) return found;
      }
      return null;
    }
    if (value && typeof value === 'object') {
      for (const [key, nested] of Object.entries(value)) {
        if (FORBIDDEN_KEYS.has(key)) return `${path}/${key}`;
        const found = visit(nested, `${path}/${key}`, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  for (const [key, value] of Object.entries(entry)) {
    if (key === 'id' || key === 'component') continue;
    if (FORBIDDEN_KEYS.has(key)) return key;
    const found = visit(value, key, 1);
    if (found) return found;
  }

  return null;
}
